import React from 'react';
import {
  TrendingUp,
  DollarSign,
  RefreshCw,
  ArrowUpRight,
  Loader2,
  ListChecks,
} from 'lucide-react';
import { SearchSelect } from '../../components/shared';
import { useAdminRevenue } from '../../hooks/useAdminRevenue';
import { PRIVATE_PAYOUT_EXCLUDED_NOTE } from '../../utils/bookingSource';
import { AdminPageHeader, AdminGuideCallout } from '../../components/admin/guidance';
import { ADMIN_PAGE_GUIDES } from '../../utils/adminPageGuides';

const AdminRevenue = () => {
  const {
    revenue,
    isLoading,
    error,
    period,
    setPeriod,
    periodOptions,
    refetch
  } = useAdminRevenue('month');

  if (error) {
    return (
      <div className="space-y-5">
        <AdminPageHeader
          title="Revenue"
          subtitle={`Track and analyze platform revenue. ${PRIVATE_PAYOUT_EXCLUDED_NOTE} for private bookings.`}
        />
        <div className="rounded-2xl border border-red-200 dark:border-red-800/50 bg-white dark:bg-gray-900 p-6 text-center shadow-sm">
          <p className="text-red-600 dark:text-red-400">Failed to load revenue data. Please try again.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <AdminPageHeader
        title="Revenue"
        subtitle={`Track and analyze platform revenue. ${PRIVATE_PAYOUT_EXCLUDED_NOTE} for private bookings.`}
        actions={
          <>
            <SearchSelect
              value={period}
              onChange={setPeriod}
              options={periodOptions}
              className="w-40"
            />
            <button
              type="button"
              onClick={() => refetch()}
              className="p-2.5 border border-gray-300 dark:border-gray-600 !rounded-xl bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          </>
        }
      />

      <AdminGuideCallout
        title={ADMIN_PAGE_GUIDES.revenue.title}
        steps={ADMIN_PAGE_GUIDES.revenue.steps}
        icon={ListChecks}
      />

      {isLoading ? (
        <div className="rounded-2xl border border-gray-200/80 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm py-20 text-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary-600 mx-auto mb-3" />
          <p className="text-sm text-gray-500 dark:text-gray-400">Loading revenue data…</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="rounded-2xl border border-gray-200/80 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm p-4 sm:p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Total Revenue</p>
                  <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400 mt-1 tabular-nums">
                    ${(revenue.overview?.total_revenue || 0).toFixed(2)}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">Platform bookings only</p>
                </div>
                <DollarSign className="w-8 h-8 text-emerald-500" />
              </div>
            </div>
            <div className="rounded-2xl border border-gray-200/80 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm p-4 sm:p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Platform Fees</p>
                  <p className="text-2xl font-bold text-primary-600 dark:text-primary-400 mt-1 tabular-nums">
                    ${(revenue.overview?.platform_fees || 0).toFixed(2)}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">Excludes private bookings</p>
                </div>
                <TrendingUp className="w-8 h-8 text-primary-500" />
              </div>
            </div>
            <div className="rounded-2xl border border-gray-200/80 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm p-4 sm:p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Net Revenue</p>
                  <p className="text-2xl font-bold text-sky-600 dark:text-sky-400 mt-1 tabular-nums">
                    ${(revenue.overview?.net_revenue || 0).toFixed(2)}
                  </p>
                </div>
                <ArrowUpRight className="w-8 h-8 text-sky-500" />
              </div>
            </div>
            <div className="rounded-2xl border border-gray-200/80 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm p-4 sm:p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Profit Margin</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-gray-50 mt-1 tabular-nums">
                    {(revenue.overview?.profit_margin || 0).toFixed(2)}%
                  </p>
                </div>
                <TrendingUp className="w-8 h-8 text-gray-400" />
              </div>
            </div>
          </div>

          {revenue.breakdown && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="rounded-2xl border border-gray-200/80 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm p-4 sm:p-5">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-50 mb-4">Revenue Breakdown</h2>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">By Payment Type</span>
                  </div>
                  {revenue.breakdown.by_payment_type && (
                    <div className="space-y-2">
                      <div className="flex justify-between p-2.5 rounded-xl bg-gray-50 dark:bg-gray-800/60">
                        <span className="text-sm text-gray-700 dark:text-gray-300">Deposit</span>
                        <span className="text-sm font-medium tabular-nums text-gray-900 dark:text-gray-50">
                          ${(revenue.breakdown.by_payment_type.deposit || 0).toFixed(2)}
                        </span>
                      </div>
                      <div className="flex justify-between p-2.5 rounded-xl bg-gray-50 dark:bg-gray-800/60">
                        <span className="text-sm text-gray-700 dark:text-gray-300">Full Payment</span>
                        <span className="text-sm font-medium tabular-nums text-gray-900 dark:text-gray-50">
                          ${(revenue.breakdown.by_payment_type.full_payment || 0).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  )}
                  {revenue.breakdown.fee_breakdown && (
                    <div className="mt-4">
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600 dark:text-gray-400">Fee Breakdown</span>
                      </div>
                      <div className="space-y-2 mt-2">
                        <div className="flex justify-between p-2.5 rounded-xl bg-gray-50 dark:bg-gray-800/60">
                          <span className="text-sm text-gray-700 dark:text-gray-300">Platform Fees</span>
                          <span className="text-sm font-medium tabular-nums text-gray-900 dark:text-gray-50">
                            ${(revenue.breakdown.fee_breakdown.platform_fees || 0).toFixed(2)}
                          </span>
                        </div>
                        <div className="flex justify-between p-2.5 rounded-xl bg-gray-50 dark:bg-gray-800/60">
                          <span className="text-sm text-gray-700 dark:text-gray-300">Processor Fees</span>
                          <span className="text-sm font-medium tabular-nums text-gray-900 dark:text-gray-50">
                            ${(revenue.breakdown.fee_breakdown.processor_fees || 0).toFixed(2)}
                          </span>
                        </div>
                        <div className="flex justify-between p-2.5 rounded-xl bg-emerald-50 dark:bg-emerald-950/30">
                          <span className="text-sm font-semibold text-gray-900 dark:text-gray-50">Net Fees</span>
                          <span className="text-sm font-bold text-emerald-600 dark:text-emerald-400 tabular-nums">
                            ${(revenue.breakdown.fee_breakdown.net_fees || 0).toFixed(2)}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {revenue.by_source && (
                <div className="rounded-2xl border border-gray-200/80 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm p-4 sm:p-5">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-50 mb-4">Top Revenue Sources</h2>
                  <div className="space-y-4">
                    {revenue.by_source.by_vendor && revenue.by_source.by_vendor.length > 0 && (
                      <div>
                        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Top Vendors</h3>
                        <div className="space-y-2">
                          {revenue.by_source.by_vendor.slice(0, 5).map((vendor, index) => (
                            <div key={index} className="flex justify-between p-2.5 rounded-xl bg-gray-50 dark:bg-gray-800/60">
                              <span className="text-sm text-gray-600 dark:text-gray-400 truncate pr-2">{vendor.business_name}</span>
                              <span className="text-sm font-medium tabular-nums text-gray-900 dark:text-gray-50 shrink-0">
                                ${parseFloat(vendor.revenue || 0).toFixed(2)}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {revenue.by_source.by_service && revenue.by_source.by_service.length > 0 && (
                      <div>
                        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Top Services</h3>
                        <div className="space-y-2">
                          {revenue.by_source.by_service.slice(0, 5).map((service, index) => (
                            <div key={index} className="flex justify-between p-2.5 rounded-xl bg-gray-50 dark:bg-gray-800/60">
                              <span className="text-sm text-gray-600 dark:text-gray-400 truncate pr-2">{service.service_name}</span>
                              <span className="text-sm font-medium tabular-nums text-gray-900 dark:text-gray-50 shrink-0">
                                ${parseFloat(service.revenue || 0).toFixed(2)}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {revenue.projections && (
            <div className="rounded-2xl border border-gray-200/80 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm p-4 sm:p-5">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-50 mb-4">Revenue Projections</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <div className="p-3 rounded-xl bg-gray-50 dark:bg-gray-800/60">
                  <p className="text-xs text-gray-600 dark:text-gray-400">Average Daily</p>
                  <p className="text-lg font-bold text-gray-900 dark:text-gray-50 tabular-nums">
                    ${(revenue.projections.average_daily_revenue || 0).toFixed(2)}
                  </p>
                </div>
                <div className="p-3 rounded-xl bg-gray-50 dark:bg-gray-800/60">
                  <p className="text-xs text-gray-600 dark:text-gray-400">Projected Monthly</p>
                  <p className="text-lg font-bold text-gray-900 dark:text-gray-50 tabular-nums">
                    ${(revenue.projections.projected_monthly || 0).toFixed(2)}
                  </p>
                </div>
                <div className="p-3 rounded-xl bg-gray-50 dark:bg-gray-800/60">
                  <p className="text-xs text-gray-600 dark:text-gray-400">Projected Quarterly</p>
                  <p className="text-lg font-bold text-gray-900 dark:text-gray-50 tabular-nums">
                    ${(revenue.projections.projected_quarterly || 0).toFixed(2)}
                  </p>
                </div>
                <div className="p-3 rounded-xl bg-gray-50 dark:bg-gray-800/60">
                  <p className="text-xs text-gray-600 dark:text-gray-400">Growth Rate</p>
                  <p className={`text-lg font-bold tabular-nums ${(revenue.projections.growth_rate || 0) >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
                    {(revenue.projections.growth_rate || 0).toFixed(2)}%
                  </p>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AdminRevenue;
